import * as React from 'react';

/**
 * HelpScout's desk shortcuts, which this desk replaced: `a` raises the assignee picker, `s` the status
 * picker, and `f` opens the composer in forward mode — acting on whatever the surface owns, the
 * selected rows in the table or the open ticket on the desk. They fire on the document, so the guard
 * below is what keeps them out of the way: a key pressed into a text field, or into a menu that
 * type-aheads on letters, belongs to that surface. Each surface claims only the keys it can act on, so
 * the two that are on screen together — the conversation and the details panel — never contend.
 */
export type TicketShortcut = 'assign' | 'status' | 'forward';

/** The shortcuts that raise a picker on a ticket field, rather than acting on the ticket directly. */
export type TicketFieldShortcut = Exclude<TicketShortcut, 'forward'>;

export const TICKET_FIELD_SHORTCUTS = ['assign', 'status'] as const;
export const TICKET_FORWARD_SHORTCUTS = ['forward'] as const;

const SHORTCUT_BY_KEY: Record<string, TicketShortcut> = {
	a: 'assign',
	s: 'status',
	f: 'forward',
};

/** An open menu, listbox or dialog owns its own keys — base-ui menus type-ahead on plain letters. */
const OWNS_ITS_KEYS = '[role="dialog"], [role="menu"], [role="listbox"], [role="alertdialog"]';

function claimedByAnotherSurface(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	const tag = target.tagName;
	if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
	return !!target.closest(OWNS_ITS_KEYS);
}

interface UseTicketShortcutsOptions<Shortcut extends TicketShortcut> {
	/** False while the surface owning these keys is absent, so nothing listens when nothing can act. */
	enabled: boolean;
	/** The keys this surface claims; every other shortcut key is left to the surface that owns it. */
	shortcuts: readonly Shortcut[];
	onShortcut: (shortcut: Shortcut) => void;
}

export function useTicketShortcuts<Shortcut extends TicketShortcut>({
	enabled,
	shortcuts,
	onShortcut,
}: UseTicketShortcutsOptions<Shortcut>) {
	const handlerRef = React.useRef(onShortcut);
	handlerRef.current = onShortcut;
	const shortcutsRef = React.useRef(shortcuts);
	shortcutsRef.current = shortcuts;

	React.useEffect(() => {
		if (!enabled) return undefined;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.defaultPrevented || event.repeat || event.isComposing) return;
			if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
			if (claimedByAnotherSurface(event.target)) return;

			const pressed = SHORTCUT_BY_KEY[event.key.toLowerCase()];
			const claimed = pressed && shortcutsRef.current.find(shortcut => shortcut === pressed);
			if (!claimed) return;

			event.preventDefault();
			handlerRef.current(claimed);
		};

		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [enabled]);
}
